// Wasabi uploader for MusicDJ Files bucket (AOSP Updater)
import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';

// MusicDJ Files bucket configuration
// Uses same credentials as main bucket since they share the same Wasabi account
const MUSICDJ_BUCKET = 'musicdj-files';
const AOSP_UPDATE_FOLDER = 'Android-Update';

// Configure the S3 client for Wasabi (MusicDJ Files bucket)
const s3Client = new S3Client({
  region: process.env.REACT_APP_WASABI_REGION,
  endpoint: `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com`,
  credentials: {
    accessKeyId: process.env.REACT_APP_WASABI_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_WASABI_SECRET_KEY,
  },
});

// Helper function to convert File to ArrayBuffer
const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file as ArrayBuffer'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Get the current AOSP version from version.txt
 * @returns {Promise<string>} Current version number
 */
export async function getAOSPVersion() {
  try {
    const url = `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${MUSICDJ_BUCKET}/${AOSP_UPDATE_FOLDER}/app-aosp-version.txt`;
    const response = await fetch(url + '?t=' + Date.now()); // Cache bust
    if (!response.ok) {
      throw new Error('Failed to fetch version file');
    }
    const text = await response.text();
    return text.trim();
  } catch (error) {
    console.error('Error fetching AOSP version:', error);
    throw new Error(`Failed to get version: ${error.message}`);
  }
}

/**
 * Upload new AOSP APK to Wasabi
 * @param {File} file - The APK file to upload
 * @returns {Promise<{publicUrl: string, fileName: string}>}
 */
export async function uploadAOSPApk(file) {
  try {
    const key = `${AOSP_UPDATE_FOLDER}/app-aosp-release.apk`;

    // Convert File to ArrayBuffer
    const fileBuffer = await fileToArrayBuffer(file);

    const command = new PutObjectCommand({
      Bucket: MUSICDJ_BUCKET,
      Key: key,
      Body: fileBuffer,
      ContentType: 'application/vnd.android.package-archive',
      ACL: 'public-read',
    });

    await s3Client.send(command);

    const publicUrl = `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${MUSICDJ_BUCKET}/${key}`;

    return {
      publicUrl,
      fileName: 'app-aosp-release.apk',
    };
  } catch (error) {
    console.error('Error uploading AOSP APK:', error);
    throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Update AOSP version number in version.txt
 * @param {string} version - The new version number
 * @returns {Promise<string>} Public URL of the version file
 */
export async function updateAOSPVersion(version) {
  try {
    const key = `${AOSP_UPDATE_FOLDER}/app-aosp-version.txt`;

    const command = new PutObjectCommand({
      Bucket: MUSICDJ_BUCKET,
      Key: key,
      Body: version.toString(),
      ContentType: 'text/plain',
      ACL: 'public-read',
    });

    await s3Client.send(command);

    const publicUrl = `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${MUSICDJ_BUCKET}/${key}`;

    return publicUrl;
  } catch (error) {
    console.error('Error updating AOSP version:', error);
    throw new Error(`Version update failed: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get the public URL for the AOSP APK
 * @returns {string}
 */
export function getAOSPApkUrl() {
  return `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${MUSICDJ_BUCKET}/${AOSP_UPDATE_FOLDER}/app-aosp-release.apk`;
}

/**
 * Get the public URL for the version file
 * @returns {string}
 */
export function getAOSPVersionUrl() {
  return `https://s3.${process.env.REACT_APP_WASABI_REGION}.wasabisys.com/${MUSICDJ_BUCKET}/${AOSP_UPDATE_FOLDER}/app-aosp-version.txt`;
}
